import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useGlobalConfig } from './GlobalContext';
import { useWidgetsSdkLoader } from 'hooks/useWidgetsSdkLoader';
import { CxWebEncryptionApis } from 'apis/cxWebEncryptionAPI';
import { extractSubdomainFromFrontendUrl } from 'utils/widgets-sdk-loader';
import { selectPilotList, selectSelectedPilotId } from 'store/dataReducers/pilotsSlice';
import { selectProjectAttributes } from 'store/dataReducers/applicationScopeSlice';

interface WidgetSdkContextValue {
  instanceId: string | null;
  isInitializing: boolean;
  initError: string | null;
  isReady: boolean;
  reinitialize: () => Promise<void>; // Force re-initialization (for refresh after save)
}

const WidgetSdkContext = createContext<WidgetSdkContextValue>({
  instanceId: null,
  isInitializing: false,
  initError: null,
  isReady: false,
  reinitialize: async () => {}, // Default no-op
});

// Module-level flag to prevent duplicate initializations
let isInitializing = false;
let initializedInstanceId: string | null = null;
let lastConfigKey: string | null = null; // Track config changes to reset state

// Kill switch: If SDK fails catastrophically, disable it completely
let sdkDisabled = false;
let sdkFailureCount = 0;
const MAX_SDK_FAILURES = 3; // Disable SDK after 3 consecutive failures

// Hardcoded values (as per requirements)
const HARDCODED_CONFIG = {
  UTILITY_ACCOUNT_IDENTIFIER: 'deto_user_account_id',
} as const;

export const WidgetSdkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { rc } = useGlobalConfig();
  const [instanceId, setInstanceId] = useState<string | null>(initializedInstanceId);
  const [isInitializingState, setIsInitializingState] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const [sdkDisabledState, setSdkDisabledState] = useState(sdkDisabled);

  // Get data from Redux
  const pilotList = useSelector(selectPilotList);
  const selectedPilotId = useSelector(selectSelectedPilotId);
  const projectAttributes = useSelector(selectProjectAttributes);

  // Get selected pilot
  const selectedPilot = useMemo(() => {
    if (!selectedPilotId || !pilotList.length) return null;
    return pilotList.find((pilot: any) => String(pilot.projectId) === String(selectedPilotId));
  }, [selectedPilotId, pilotList]);

  // Build widget SDK config from Redux state
  const widgetSdkConfig = useMemo(() => {
    // Get environment (prefer environment over projectEnvironments for backward compatibility)
    const environment = selectedPilot?.environment || selectedPilot?.projectEnvironments?.[0];
    
    // Extract stack_name for CLIENT_ID
    const stackName = environment?.stack_name || '';
    const clientId = stackName ? `${stackName}-dashboard` : '';

    // Extract data_server_url for API_URL
    const apiUrl = environment?.data_server_url || '';

    // Extract fuel_type from projectAttributes
    let fuelType = 'ELECTRIC'; // Default fallback
    if (projectAttributes?.fuel_type) {
      if (typeof projectAttributes.fuel_type === 'string') {
        const fuelTypes = projectAttributes.fuel_type.split(',').map(f => f.trim());
        fuelType = fuelTypes[0]?.toUpperCase() || 'ELECTRIC';
      } else if (Array.isArray(projectAttributes.fuel_type)) {
        const fuelTypeArray = projectAttributes.fuel_type as any[];
        if (fuelTypeArray.length > 0) {
          fuelType = String(fuelTypeArray[0]).toUpperCase();
        }
      }
    }

    // Extract customer_type for ACCOUNT_TYPE
    let accountType = 'RESIDENTIAL'; // Default fallback
    if (projectAttributes?.customer_type) {
      accountType = String(projectAttributes.customer_type).toUpperCase();
    }

    const environmentName = process.env.REACT_APP_TOOL_API_URL?.includes('deto-prod-api.bidgely.com') ? 'uat' : 'dev';
    // Extract pilotId
    const pilotId = selectedPilot?.pilotId || selectedPilot?.projectId || '';

    // Extract subdomain from frontend_url
    const frontendUrl = selectedPilot?.environment?.fronted_url;
    const subdomain = extractSubdomainFromFrontendUrl(frontendUrl);

    // Create a config key to detect changes
    const trackingKey = `${clientId}-${apiUrl}-${pilotId}-${environmentName}-${subdomain || ''}`;

    return {
      CLIENT_ID: clientId,
      API_URL: apiUrl,
      UTILITY_ACCOUNT_IDENTIFIER: HARDCODED_CONFIG.UTILITY_ACCOUNT_IDENTIFIER,
      FUEL_TYPE: fuelType,
      ACCOUNT_TYPE: accountType,
      ENVIRONMENT: environmentName,
      PILOT_ID: pilotId,
      SUBDOMAIN: subdomain, // Add subdomain to config
      SCENARIO_ID: 'MS_1',
      _trackingKey: trackingKey, // Internal key for change detection
    };
  }, [selectedPilot, projectAttributes]);

  // Load SDK script first - using values from Redux
  // Don't load SDK if it's been disabled due to repeated failures or if config is not ready
  const { isLoading: sdkLoading, isLoaded: sdkLoaded, error: sdkLoadError } = useWidgetsSdkLoader(
    sdkDisabled || !widgetSdkConfig.ENVIRONMENT || !widgetSdkConfig.PILOT_ID 
      ? null 
      : { 
          environment: widgetSdkConfig.ENVIRONMENT, 
          pilotId: widgetSdkConfig.PILOT_ID,
          subdomain: widgetSdkConfig.SUBDOMAIN || undefined
        },
    {
      autoLoad: true,
      onError: (error: Error) => {
        console.error('[WidgetSdkContext] SDK loading failed:', error);
        sdkFailureCount++;
        
        // Disable SDK if it fails too many times
        if (sdkFailureCount >= MAX_SDK_FAILURES) {
          console.warn(`[WidgetSdkContext] SDK disabled after ${sdkFailureCount} failures. Widgets will not be available.`);
          sdkDisabled = true;
          setSdkDisabledState(true);
          // Remove SDK script from DOM to prevent further errors
          try {
            const scripts = document.querySelectorAll('script[src*="widgets.bidgely.com"]');
            scripts.forEach(script => script.remove());
            if (window.BidgelyWebSdk) {
              delete (window as any).BidgelyWebSdk;
            }
          } catch (cleanupError) {
            console.error('[WidgetSdkContext] Error cleaning up SDK:', cleanupError);
          }
        }
        
        setInitError(`SDK loading failed: ${error.message}`);
      },
    }
  );

  // Note: We intentionally do NOT build the payload in the browser anymore.
  // Proxypulse returns encryptedPayload (and injects pingpong token server-side).

  const initializeSdk = useCallback(async () => {
    // Don't initialize if SDK is disabled
    if (sdkDisabled) {
      console.warn('[WidgetSdkContext] SDK initialization skipped - SDK is disabled');
      return;
    }

    // Check if config has changed - if so, reset state
    const currentTrackingKey = widgetSdkConfig._trackingKey;
    const configChanged = lastConfigKey && lastConfigKey !== currentTrackingKey;
    
    if (configChanged) {
      console.log('[WidgetSdkContext] Config changed, resetting initialization state', {
        oldKey: lastConfigKey,
        newKey: currentTrackingKey
      });
      // Reset module-level state for new config
      isInitializing = false;
      initializedInstanceId = null;
      setInstanceId(null);
      setIsInitializingState(false);
      setInitError(null);
    }

    // Check if already initializing
    if (isInitializing) {
      console.log('[WidgetSdkContext] Initialization already in progress, skipping');
      return;
    }

    // If we have an instanceId and config hasn't changed, don't re-initialize
    if (initializedInstanceId && !configChanged) {
      console.log('[WidgetSdkContext] SDK already initialized with current config, skipping');
      return;
    }

    // Update lastConfigKey before proceeding
    lastConfigKey = currentTrackingKey;

    isInitializing = true;
    setIsInitializingState(true);
    setInitError(null);

    try {
      // Check if config is ready before initializing
      if (!widgetSdkConfig.CLIENT_ID || !widgetSdkConfig.API_URL || !widgetSdkConfig.PILOT_ID) {
        console.warn('[WidgetSdkContext] SDK initialization skipped - configuration not ready');
        isInitializing = false;
        setIsInitializingState(false);
        return;
      }

      let encryptedPayload: string;
      const initPayloadResp = await CxWebEncryptionApis.getWidgetsSdkInitPayload(
        widgetSdkConfig.PILOT_ID,
        widgetSdkConfig.CLIENT_ID,
        {
          utilityAccountIdentifier: widgetSdkConfig.UTILITY_ACCOUNT_IDENTIFIER,
          fuelType: widgetSdkConfig.FUEL_TYPE,
          accountType: widgetSdkConfig.ACCOUNT_TYPE,
        }
      );

      if (initPayloadResp.error) {
        const msg = initPayloadResp.error.message || 'Failed to get encrypted init payload';
        console.error('[WidgetSdkContext] init-payload failed:', initPayloadResp.error);
        setInitError(msg);
        isInitializing = false;
        setIsInitializingState(false);
        return;
      }

      encryptedPayload = initPayloadResp.data!.encryptedPayload;

      await new Promise<void>((resolve, reject) => {
        try {
          if (!window.BidgelyWebSdk || !window.BidgelyWebSdk.initialize) {
            throw new Error('BidgelyWebSdk.initialize is not available');
          }

          // Wrap SDK initialize in try-catch to prevent unhandled errors
          try {
            window.BidgelyWebSdk.initialize(
              'PROD',
              {
                client_id: widgetSdkConfig.CLIENT_ID,
                api_url: widgetSdkConfig.API_URL,
                renderWidgetsImmediate: false,
                relayFileDownloads: true,
                payload: encryptedPayload,
                scenarioId: widgetSdkConfig.SCENARIO_ID,
              },  
              (response: any) => {
                try {
                  if (response.messageType === 'ERROR') {
                    const msg =
                      typeof response.data === 'string'
                        ? response.data
                        : 'Widget SDK initialization failed';
                    setInitError(msg);
                    isInitializing = false;
                    setIsInitializingState(false);
                    reject(new Error(msg));
                    return;
                  }

                  if (
                    response.messageType === 'WIDGET_UI_READY' ||
                    response.messageType === 'SUCCESS'
                  ) {
                    const instanceId = response.data?.instanceId as string | undefined;
                    if (instanceId) {
                      initializedInstanceId = instanceId;
                      setInstanceId(instanceId);
                      console.log('[WidgetSdkContext] SDK initialized with instanceId:', instanceId);
                    }
                    isInitializing = false;
                    setIsInitializingState(false);
                    resolve();
                  }
                } catch (callbackErr) {
                  console.error('[WidgetSdkContext] Error in initialize callback:', callbackErr);
                  isInitializing = false;
                  setIsInitializingState(false);
                  // Don't reject - just set error state to prevent page crash
                  setInitError(`SDK callback error: ${callbackErr instanceof Error ? callbackErr.message : 'Unknown error'}`);
                  resolve(); // Resolve instead of reject to prevent unhandled promise rejection
                }
              }
            );
          } catch (sdkErr) {
            console.error('[WidgetSdkContext] SDK initialize call error:', sdkErr);
            isInitializing = false;
            setIsInitializingState(false);
            setInitError(`SDK initialization error: ${sdkErr instanceof Error ? sdkErr.message : 'Unknown error'}`);
            reject(sdkErr);
          }
        } catch (initErr) {
          console.error('[WidgetSdkContext] Error calling initialize:', initErr);
          isInitializing = false;
          setIsInitializingState(false);
          setInitError(`Initialization error: ${initErr instanceof Error ? initErr.message : 'Unknown error'}`);
          reject(initErr);
        }
      });
    } catch (err) {
      // Final catch-all to ensure we never leave the app in a broken state
      const message =
        err instanceof Error ? err.message : 'Unknown error initializing widget SDK';
      console.error('[WidgetSdkContext] Initialization error', err);
      
      sdkFailureCount++;
      
      // Disable SDK if it fails too many times
      if (sdkFailureCount >= MAX_SDK_FAILURES) {
        console.warn(`[WidgetSdkContext] SDK disabled after ${sdkFailureCount} initialization failures. Widgets will not be available.`);
        sdkDisabled = true;
        setSdkDisabledState(true);
        // Remove SDK script from DOM to prevent further errors
        try {
          const scripts = document.querySelectorAll('script[src*="widgets.bidgely.com"]');
          scripts.forEach(script => script.remove());
          if (window.BidgelyWebSdk) {
            delete (window as any).BidgelyWebSdk;
          }
        } catch (cleanupError) {
          console.error('[WidgetSdkContext] Error cleaning up SDK:', cleanupError);
        }
      }
      
      setInitError(message);
      isInitializing = false;
      setIsInitializingState(false);
      // Don't throw - just set error state
    }
  }, [widgetSdkConfig]);

  // Force re-initialization method - resets instanceId and re-initializes SDK
  // This is used when template changes (menu item click) or refreshKey changes (after save) to get fresh widget data
  const reinitialize = useCallback(async () => {
    // Don't re-initialize if SDK is disabled
    // Note: sdkDisabled is a module-level variable, not React state, so we check it directly
    // eslint-disable-next-line react-hooks/exhaustive-deps
    if (sdkDisabled) {
      console.warn('[WidgetSdkContext] Re-initialization skipped - SDK is disabled');
      return;
    }

    // Wait for SDK to be loaded if it's still loading
    if (sdkLoading || !sdkLoaded) {
      console.log('[WidgetSdkContext] Waiting for SDK to load before re-initializing');
      // Wait up to 10 seconds for SDK to load
      let waitCount = 0;
      while ((sdkLoading || !sdkLoaded) && waitCount < 100) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitCount++;
      }
      if (sdkLoading || !sdkLoaded) {
        console.warn('[WidgetSdkContext] SDK still loading after wait, proceeding anyway');
      }
    }

    // Check if SDK is available
    if (!window.BidgelyWebSdk) {
      console.error('[WidgetSdkContext] Cannot re-initialize - BidgelyWebSdk not available');
      setInitError('BidgelyWebSdk not available for re-initialization');
      return;
    }

    // Wait for any ongoing initialization to complete
    if (isInitializing) {
      console.log('[WidgetSdkContext] Waiting for ongoing initialization to complete before re-initializing');
      // Wait up to 5 seconds for initialization to complete
      let waitCount = 0;
      while (isInitializing && waitCount < 50) {
        await new Promise(resolve => setTimeout(resolve, 100));
        waitCount++;
      }
      if (isInitializing) {
        console.warn('[WidgetSdkContext] Initialization still in progress after wait, proceeding anyway');
      }
    }

    // Reset instanceId and lastConfigKey to force re-initialization
    // Clearing lastConfigKey ensures initializeSdk will treat this as a config change
    console.log('[WidgetSdkContext] Resetting instanceId and config tracking to force re-initialization');
    const previousConfigKey = lastConfigKey;
    lastConfigKey = null; // Clear to force re-initialization
    initializedInstanceId = null;
    setInstanceId(null);
    setIsInitializingState(false);
    setInitError(null);
    isInitializing = false; // Reset flag to allow new initialization

    // Trigger re-initialization
    await initializeSdk();
  }, [initializeSdk, sdkLoaded, sdkLoading]);

  // Handle SDK loading errors
  useEffect(() => {
    if (sdkLoadError) {
      setInitError(`SDK loading failed: ${sdkLoadError.message}`);
    }
  }, [sdkLoadError]);

  // Initialize SDK after it's loaded
  useEffect(() => {
    // Don't initialize if SDK is disabled
    if (sdkDisabled) {
      return;
    }

    // Wait for SDK to be loaded
    if (!sdkLoaded || sdkLoading) {
      return;
    }

    // Check if SDK is available
    if (!window.BidgelyWebSdk) {
      console.warn('[WidgetSdkContext] SDK loaded but BidgelyWebSdk not available on window');
      setInitError('BidgelyWebSdk not available after SDK script loaded');
      return;
    }

    // Check if config is ready
    if (!widgetSdkConfig.CLIENT_ID || !widgetSdkConfig.API_URL || !widgetSdkConfig.PILOT_ID) {
      console.warn('[WidgetSdkContext] Config not ready for initialization', {
        hasClientId: !!widgetSdkConfig.CLIENT_ID,
        hasApiUrl: !!widgetSdkConfig.API_URL,
        hasPilotId: !!widgetSdkConfig.PILOT_ID,
        environment: widgetSdkConfig.ENVIRONMENT
      });
      return;
    }

    // Check if config has changed - if so, we need to re-initialize
    const currentTrackingKey = widgetSdkConfig._trackingKey;
    const needsInitialization = 
      !initializedInstanceId || // Never initialized
      lastConfigKey !== currentTrackingKey; // Config changed

    if (needsInitialization && !isInitializing) {
      console.log('[WidgetSdkContext] Triggering SDK initialization', {
        hasInstanceId: !!initializedInstanceId,
        configKey: currentTrackingKey,
        lastConfigKey: lastConfigKey
      });
      initializeSdk();
    } else if (initializedInstanceId && lastConfigKey === currentTrackingKey) {
      console.log('[WidgetSdkContext] SDK already initialized with current config');
    }
  }, [sdkLoaded, sdkLoading, initializeSdk, sdkDisabled, widgetSdkConfig]);

  // Add global error handler for unhandled widget errors
  // This prevents SDK errors from crashing the entire application
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      try {
        // "Script error." is a CORS error from cross-origin scripts - catch all of these
        const isScriptError = event.message === 'Script error.' || event.message === '';
        const isWidgetRelated = 
          event.message?.includes('BidgelyWebSdk') ||
          event.message?.includes('widget') ||
          event.message?.includes('bundle.js') ||
          event.filename?.includes('bundle.js') ||
          event.filename?.includes('widgets.bidgely.com') ||
          event.filename?.includes('wc-sdk') ||
          event.filename?.includes('bidgely.com');

        // Catch script errors from widget domains (CORS errors)
        if (isScriptError && (isWidgetRelated || event.filename?.includes('bidgely.com'))) {
          console.warn('[WidgetSdkProvider] Widget script error (CORS) caught and suppressed:', {
            message: event.message,
            filename: event.filename,
            lineno: event.lineno
          });
          event.preventDefault();
          event.stopPropagation();
          setInitError('Widget SDK script error (likely CORS issue). Widget may not function correctly.');
          return false;
        }

        // Check if error is related to widget SDK
        if (isWidgetRelated) {
          console.error('[WidgetSdkProvider] Global widget error caught:', event);
          // Prevent error from breaking the page
          event.preventDefault();
          event.stopPropagation();
          setInitError(`Widget error: ${event.message || 'Unknown error'}`);
          return false;
        }
      } catch (handlerError) {
        // If the error handler itself throws, log it but don't let it break the app
        console.error('[WidgetSdkProvider] Error in error handler (this should not happen):', handlerError);
        // Still try to prevent the original error
        try {
          event.preventDefault();
          event.stopPropagation();
        } catch {
          // Ignore if preventDefault fails
        }
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      try {
        // Check if rejection is related to widget SDK
        const reason = event.reason;
        const reasonMessage = reason?.message || String(reason || '');
        const isWidgetRelated = 
          reasonMessage.includes('BidgelyWebSdk') ||
          reasonMessage.includes('widget') ||
          reasonMessage.includes('bundle.js') ||
          reasonMessage.includes('encryption') ||
          reasonMessage.includes('WidgetSdk') ||
          reasonMessage.includes('Script error') ||
          (typeof reason === 'string' && reason.includes('widget'));

        if (isWidgetRelated) {
          console.error('[WidgetSdkProvider] Unhandled widget promise rejection:', reason);
          event.preventDefault();
          event.stopPropagation();
          setInitError(`Widget error: ${reasonMessage || 'Promise rejection'}`);
        }
      } catch (handlerError) {
        // If the rejection handler itself throws, log it but don't let it break the app
        console.error('[WidgetSdkProvider] Error in rejection handler (this should not happen):', handlerError);
        // Still try to prevent the original rejection
        try {
          event.preventDefault();
          event.stopPropagation();
        } catch {
          // Ignore if preventDefault fails
        }
      }
    };

    // Use capture phase to catch errors early
    window.addEventListener('error', handleError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);

    return () => {
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);
    };
  }, []);

  const value: WidgetSdkContextValue = {
    instanceId: sdkDisabled ? null : instanceId,
    isInitializing: sdkDisabled ? false : isInitializingState,
    initError: sdkDisabled ? 'Widget SDK has been disabled due to repeated failures' : initError,
    isReady: sdkDisabled ? false : !!instanceId && !isInitializingState && !initError,
    reinitialize: reinitialize,
  };

  return <WidgetSdkContext.Provider value={value}>{children}</WidgetSdkContext.Provider>;
};

export const useWidgetSdk = () => useContext(WidgetSdkContext);

